// One row, opened in a centred dialog over the log: every field it carries, each id whole with a way to copy
// it, what changed laid side by side (already masked by the server — rendered, never unmasked), and,
// when a request wrote it, the way to see everything that request did (the trace).

import type { AuditEntry } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, Table, type TableColumn } from '@clavian/ui-web';
import { useEffect, useState, type ReactNode } from 'react';

import { Facts } from '../../ui';
import { traceIdOf, traceUrlOf } from './trace';
import type { AuditTimes } from './time';
import type { AuditLabels, Directory } from './labels';
import { Actor } from './rows';

type IdName = 'actorId' | 'onBehalfOf' | 'venueId' | 'targetId' | 'requestId' | 'traceId' | 'rowId';

/** A whole id, and a button that copies it. */
function FullId({ id, name }: { readonly id: string; readonly name: IdName }): ReactNode {
  const { t } = useTranslation('console');
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <span className="console-audit-full-id">
      <code>{id}</code>
      <Button
        variant="ghost"
        size="sm"
        aria-label={t('audit.detail.copyNamed', { name: t(`audit.detail.names.${name}`) })}
        onClick={() => {
          void navigator.clipboard?.writeText(id).then(
            () => setCopied(true),
            () => undefined,
          );
        }}
      >
        {t('audit.detail.copy')}
      </Button>
      <span role="status" className="console-audit-copied">
        {copied ? t('audit.detail.copied') : ''}
      </span>
    </span>
  );
}

/** A name over its id, or the id alone. */
function Named({ name, id, idName }: { readonly name: string | undefined; readonly id: string; readonly idName: IdName }): ReactNode {
  return (
    <span className="console-audit-named">
      {name === undefined ? null : <span>{name}</span>}
      <FullId id={id} name={idName} />
    </span>
  );
}

function Group({ title, children }: { readonly title: ReactNode; readonly children: ReactNode }): ReactNode {
  return (
    <section className="console-audit-group">
      <h3 className="console-audit-group__title">{title}</h3>
      {children}
    </section>
  );
}

type DiffRow = { readonly field: string; readonly before: string; readonly after: string; readonly changed: boolean };

const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> => typeof value === 'object' && value !== null && !Array.isArray(value);
const shown = (value: unknown): string => (value === undefined ? '' : JSON.stringify(value));

/** What changed, as recorded — personal data already masked by the audit service (spec/07 §11). */
function Changes({ row }: { readonly row: AuditEntry }): ReactNode {
  const { t } = useTranslation('console');
  const before = row.before ?? null;
  const after = row.after ?? null;
  if (before === null && after === null) return <p className="console-muted">{t('audit.changes.nothing')}</p>;

  // Two objects (or one, and nothing): field by field, the changed ones marked.
  if ((before === null || isRecord(before)) && (after === null || isRecord(after))) {
    const left = before ?? {};
    const right = after ?? {};
    const fields = [...new Set([...Object.keys(left), ...Object.keys(right)])];
    const rows: DiffRow[] = fields.map((field) => ({
      field,
      before: shown(left[field]),
      after: shown(right[field]),
      changed: shown(left[field]) !== shown(right[field]),
    }));
    const columns: readonly TableColumn<DiffRow>[] = [
      { key: 'field', header: t('audit.changes.field'), cell: (diff) => <code>{diff.field}</code> },
      { key: 'before', header: t('audit.changes.before'), cell: (diff) => <code className={diff.changed ? 'console-audit-diff--before' : 'console-audit-diff--same'}>{diff.before || '—'}</code> },
      { key: 'after', header: t('audit.changes.after'), cell: (diff) => <code className={diff.changed ? 'console-audit-diff--after' : 'console-audit-diff--same'}>{diff.after || '—'}</code> },
    ];
    return <Table className="console-audit-diff" caption={t('audit.changes.caption')} captionHidden columns={columns} rows={rows} rowKey={(diff) => diff.field} />;
  }

  return (
    <div className="console-audit-raw">
      {before === null ? null : (
        <>
          <p className="console-audit-raw__label">{t('audit.changes.before')}</p>
          <pre>{JSON.stringify(before, null, 2)}</pre>
        </>
      )}
      {after === null ? null : (
        <>
          <p className="console-audit-raw__label">{t('audit.changes.after')}</p>
          <pre>{JSON.stringify(after, null, 2)}</pre>
        </>
      )}
    </div>
  );
}

export interface RowDetailProps {
  readonly row: AuditEntry | null;
  readonly labels: AuditLabels;
  readonly directory: Directory;
  readonly times: AuditTimes;
  /** When the rows arrived, as the table counts from it. */
  readonly now: number;
  readonly onClose: () => void;
  readonly onTrace: (requestId: string) => void;
}

export function RowDetail({ row, labels, directory, times, now, onClose, onTrace }: RowDetailProps): ReactNode {
  const { t } = useTranslation('console');
  // Kept while the dialog closes, so it does not empty as it fades away.
  const [last, setLast] = useState<AuditEntry | null>(row);
  if (row !== null && row !== last) setLast(row);
  const shownRow = row ?? last;
  if (shownRow === null) return null;
  const traceUrl = shownRow.requestId === null ? undefined : traceUrlOf(shownRow.requestId);
  const traceId = shownRow.requestId === null ? undefined : traceIdOf(shownRow.requestId);

  return (
    <Dialog
      open={row !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="lg"
      eyebrow={times.relative(shownRow.occurredAt, now)}
      title={labels.action(shownRow.action)}
      description={<code className="console-audit-code">{shownRow.action}</code>}
      className="console-audit-detail"
    >
      <div className="console-audit-groups">
        <Group title={t('audit.detail.when')}>
          <Facts
            items={[
              { key: 'local', label: t('audit.detail.whenLocal'), value: <time dateTime={shownRow.occurredAt}>{times.local(shownRow.occurredAt)}</time> },
              { key: 'utc', label: t('audit.detail.whenUtc'), value: <time dateTime={shownRow.occurredAt}>{times.utc(shownRow.occurredAt)}</time> },
              ...(shownRow.recordedAt === shownRow.occurredAt
                ? []
                : [{ key: 'recorded', label: t('audit.detail.recordedAt'), value: <time dateTime={shownRow.recordedAt}>{times.utc(shownRow.recordedAt)}</time> }]),
            ]}
          />
        </Group>

        <Group title={t('audit.detail.who')}>
          <Actor row={shownRow} full />
          <Facts
            columns={1}
            items={[
              ...(shownRow.actor.id === null ? [] : [{ key: 'id', label: t('audit.detail.actorId'), value: <FullId id={shownRow.actor.id} name="actorId" /> }]),
              ...(shownRow.onBehalfOf === undefined
                ? []
                : [
                    {
                      key: 'grant',
                      label: t('audit.detail.onBehalfOf'),
                      value: (
                        <span className="console-audit-named">
                          <span>{t(`audit.onBehalfOf.${shownRow.onBehalfOf.kind}`)}</span>
                          <FullId id={shownRow.onBehalfOf.id} name="onBehalfOf" />
                        </span>
                      ),
                    },
                  ]),
              ...(shownRow.actorIp === null ? [] : [{ key: 'ip', label: t('audit.detail.actorIp'), value: <code>{shownRow.actorIp}</code> }]),
            ]}
          />
        </Group>

        <Group title={t('audit.detail.where')}>
          <Facts
            columns={1}
            items={[
              {
                key: 'venue',
                label: t('audit.detail.venue'),
                value:
                  shownRow.venueId === null ? (
                    <span className="console-muted">{t('audit.acrossOrganization')}</span>
                  ) : (
                    <Named name={directory.venue(shownRow.venueId)} id={shownRow.venueId} idName="venueId" />
                  ),
              },
            ]}
          />
        </Group>

        <Group title={t('audit.detail.about')}>
          <Facts
            columns={1}
            items={[
              {
                key: 'target',
                label: t('audit.detail.target'),
                value: shownRow.targetId === null ? labels.targetType(shownRow.targetType) : <Named name={labels.targetType(shownRow.targetType)} id={shownRow.targetId} idName="targetId" />,
              },
              ...(shownRow.reason === null ? [] : [{ key: 'reason', label: t('audit.detail.reason'), value: <span className="console-audit-reason">{shownRow.reason}</span> }]),
            ]}
          />
        </Group>

        <Group title={t('audit.changes.summary')}>
          <Changes row={shownRow} />
        </Group>

        <Group title={t('audit.detail.request')}>
          {shownRow.requestId === null ? (
            <p className="console-muted">{t('audit.detail.noRequest')}</p>
          ) : (
            <>
              <Facts
                columns={1}
                items={[
                  { key: 'request', label: t('audit.detail.requestId'), value: <FullId id={shownRow.requestId} name="requestId" /> },
                  ...(traceId === undefined ? [] : [{ key: 'trace', label: t('audit.detail.traceId'), value: <FullId id={traceId} name="traceId" /> }]),
                ]}
              />
              <div className="console-audit-trace-actions">
                <Button size="sm" variant="primary" onClick={() => onTrace(shownRow.requestId as string)}>
                  {t('audit.detail.showTrace')}
                </Button>
                {traceUrl === undefined ? null : (
                  <a href={traceUrl} target="_blank" rel="noreferrer">
                    {t('audit.trace.open')}
                  </a>
                )}
              </div>
            </>
          )}
        </Group>

        <Group title={t('audit.detail.rowId')}>
          <FullId id={shownRow.id} name="rowId" />
        </Group>
      </div>
    </Dialog>
  );
}
